import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useProfile } from "@/entities/profile";
import { useUpdateProfile, useUploadAvatar } from "@/features/profile-settings";
import { ApiError } from "@/shared/api";
import { Avatar, Button, useToast } from "@/shared/ui";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * Имя и аватарка (правки заказчика 07.09.2026).
 *
 * До этого экрана профиль целиком приходил из Telegram и менять в нём было
 * нечего — по карточке пользователя нажимали, и ничего не происходило.
 *
 * Оба поля независимы и сохраняются по отдельности: аватарка уходит сразу
 * после выбора файла (второй шаг «а теперь нажмите сохранить» после диалога
 * выбора никто не ждёт), имя — по кнопке, потому что набор текста заканчивается
 * не по событию, а по решению человека.
 *
 * Кастомные имя и фото видит только он сам: в рейтинге и отзывах остаются
 * данные из Telegram. Об этом сказано подписью под полем, а не умолчанием —
 * иначе экран обещает больше, чем делает.
 */

/** Тот же потолок, что и на бэкенде (`schemas/cabinet/profile.MAX_NAME_LENGTH`). */
const MAX_NAME_LENGTH = 32;

/** Тот же потолок, что и на бэкенде (`utils/media.MAX_IMAGE_BYTES`). */
const MAX_AVATAR_BYTES = 512 * 1024;

const ACCEPTED_TYPES = "image/png,image/jpeg,image/webp";

export function ProfileEditPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { data, isPending, isError, refetch } = useProfile();
  const update = useUpdateProfile();
  const upload = useUploadAvatar();
  const fileInput = useRef<HTMLInputElement>(null);
  // `undefined` — поле ещё не трогали: тогда в нём стоит серверное значение.
  // Пустая строка — законный ввод (человек стёр имя), и подменять её значением
  // с сервера нельзя, иначе поле «само себя допечатывает».
  const [draft, setDraft] = useState<string | undefined>(undefined);

  if (isPending) return <ScreenSkeleton blocks={[160, 120]} />;

  if (isError) {
    return (
      <ScreenState
        tone="loss"
        icon="warningFill"
        title={t("states.error.title")}
        body={t("states.error.body")}
        action={{ label: t("states.error.action"), onClick: () => void refetch() }}
      />
    );
  }

  const { user } = data;
  const name = draft ?? user.display_name;
  const trimmed = name.trim();
  const busy = update.isPending || upload.isPending;

  const failed = (error: unknown) => {
    toast.show({
      tone: "loss",
      title: t("states.error.title"),
      // 501 — не поломка, а выключенное хранилище: человеку надо сказать, что
      // дело не в его файле, иначе он будет пробовать другие картинки.
      meta:
        error instanceof ApiError && error.code === "NOT_IMPLEMENTED"
          ? t("profile.edit.storageOff")
          : undefined,
    });
  };

  const pick = (file: File | undefined) => {
    if (!file) return;
    // Проверяем до отправки: 422 после загрузки большого файла по мобильной
    // сети — худший момент, чтобы узнать про предел.
    if (file.size > MAX_AVATAR_BYTES) {
      toast.show({ tone: "loss", title: t("profile.edit.tooLarge") });
      return;
    }
    upload.mutate(file, {
      onSuccess: () => {
        toast.show({ tone: "profit", title: t("profile.edit.avatarSaved") });
      },
      onError: failed,
    });
  };

  const save = (value: string) => {
    update.mutate(
      { display_name: value },
      {
        onSuccess: () => {
          setDraft(undefined);
          toast.show({ tone: "profit", title: t("profile.edit.nameSaved") });
        },
        onError: failed,
      },
    );
  };

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <TopBar title={t("profile.edit.title")} subtitle={t("profile.edit.subtitle")} />

      <section className="flex flex-col items-center gap-3 rounded-2xl border border-border-subtle bg-background-elevated p-5">
        <Avatar src={user.avatar_url} initial={user.initial} size={88} />

        <input
          ref={fileInput}
          type="file"
          accept={ACCEPTED_TYPES}
          className="sr-only"
          aria-label={t("profile.edit.avatarAction")}
          onChange={(event) => {
            pick(event.target.files?.[0]);
            // Сбрасываем значение: иначе повторный выбор того же файла после
            // отказа не даёт события change и кнопка «перестаёт работать».
            event.target.value = "";
          }}
        />

        <div className="flex w-full flex-col gap-2">
          <Button
            variant="secondary"
            block
            disabled={busy}
            onClick={() => fileInput.current?.click()}
          >
            {upload.isPending ? t("profile.edit.avatarUploading") : t("profile.edit.avatarAction")}
          </Button>

          {user.has_custom_avatar ? (
            <Button
              variant="ghost"
              block
              disabled={busy}
              onClick={() => {
                update.mutate(
                  { reset_avatar: true },
                  { onError: failed },
                );
              }}
            >
              {t("profile.edit.avatarReset")}
            </Button>
          ) : null}
        </div>

        <span className="text-caption font-normal text-text-tertiary">
          {t("profile.edit.avatarHint")}
        </span>
      </section>

      <section className="flex flex-col gap-3 rounded-2xl border border-border-subtle bg-background-elevated p-4">
        <label className="flex flex-col gap-2">
          <span className="text-caption font-medium text-text-secondary">
            {t("profile.edit.nameLabel")}
          </span>
          <input
            value={name}
            maxLength={MAX_NAME_LENGTH}
            placeholder={t("profile.edit.namePlaceholder")}
            onChange={(event) => setDraft(event.target.value)}
            className="rounded-xl border border-border-subtle bg-background-raised px-3 py-3 text-body text-text-primary outline-none placeholder:text-text-tertiary"
          />
        </label>

        <span className="self-end text-micro font-medium tabular-nums text-text-tertiary">
          {name.length} / {MAX_NAME_LENGTH}
        </span>

        <Button
          size="cta"
          block
          disabled={busy || trimmed.length === 0 || trimmed === user.display_name}
          onClick={() => save(trimmed)}
        >
          {t("common.save")}
        </Button>

        {user.has_custom_name ? (
          <Button
            variant="ghost"
            block
            disabled={busy}
            onClick={() => {
              setDraft(undefined);
              save("");
            }}
          >
            {t("profile.edit.nameReset")}
          </Button>
        ) : null}

        <span className="text-caption font-normal text-text-tertiary">
          {t("profile.edit.nameHint")}
        </span>
      </section>
    </div>
  );
}
